import type {
  BloominCardRegistrationPayload,
  CardRegistrationSession,
} from '@/lib/api/types';
import { detectCardBrand, isExpiryInFuture, isValidCvv, isValidLuhn } from '@/utils/cardValidation';

export { isValidLuhn };

export type BloominCardFormState = {
  cardNumber: string;
  expiryMonth: string;
  expiryYear: string;
  cvv: string;
  holder: string;
};

export const initialBloominCardForm: BloominCardFormState = {
  cardNumber: '',
  expiryMonth: '',
  expiryYear: '',
  cvv: '',
  holder: '',
};

export type BloominValidationMessages = {
  invalidCard: string;
  invalidExpiry: string;
  invalidCvv: string;
  invalidHolder: string;
};

/**
 * 필드 단위 검증 에러 (#430 — aria-invalid / aria-describedby 매핑용).
 *
 * 만료 월/연도는 하나의 `expiry` 에러를 공유한다.
 */
export type BloominCardFieldErrors = {
  cardNumber?: string;
  expiry?: string;
  cvv?: string;
  holder?: string;
};

type BloominPayloadBuildResult =
  | { payload: BloominCardRegistrationPayload; errors: null }
  | { payload: null; errors: BloominCardFieldErrors };

export const digitsOnly = (value: string) => value.replace(/\D/g, '');

/**
 * 만료 연도 정규화 — 2자리(`YY`)는 `20YY` 로, 4자리(`YYYY`)는 그대로, 그 외는 NaN.
 *
 * 등록(AddCardModal)·결제(BloominCardInputForm) 양 경로가 동일 규칙을 공유하도록
 * 단일 SSOT 로 노출한다. 입력에서 숫자 외 문자는 제거한 뒤 길이로만 분기한다.
 */
export const normalizeExpiryYear = (value: string): number => {
  const raw = digitsOnly(value);
  if (raw.length === 2) {
    return Number(`20${raw}`);
  }
  if (raw.length === 4) {
    return Number(raw);
  }
  return Number.NaN;
};

export const formatCardNumber = (value: string) =>
  digitsOnly(value)
    .slice(0, 19)
    .replace(/(\d{4})(?=\d)/g, '$1 ')
    .trim();

/**
 * 카드 등록 세션을 카드 입력 폼으로 렌더링할 수 있는지 판정한다.
 *
 * - Bloomin: hosted widget 미제공 → 자체 카드 입력 폼. admin 이 반환한
 *   `requires_card_input`(=requiresCardInput) 계약을 단일 권위로 신뢰한다.
 *   admin 이 명시적으로 `false` 를 반환할 때만 폼을 차단한다. 미설정(undefined)은
 *   의도된 fail-open — 필드를 보내지 않는 구버전 admin 응답에서 폼을 막으면
 *   #696 회귀(카드 입력 전 즉시 실패)가 재현되며, 실제 등록 가능 여부는
 *   서버 토큰화 엔드포인트가 최종 강제한다.
 *
 * 과거에는 `VITE_BLOOMIN_CARD_INPUT_ENABLED` + 비-prod 런타임 게이트로
 * 회원 등록 폼을 추가 차단했으나(skeleton 단계 안전장치), Bloomin 이 prod 에서
 * live 활성화된 뒤에는 이 게이트가 회원 카드 등록을 카드 입력 *전* 즉시
 * "등록 실패" 로 빠뜨리는 회귀를 유발했다. 비회원 직접 카드결제
 * (BloominCardInputForm)는 동일 게이트가 없어 prod 에서 정상 동작하므로,
 * 회원 등록 경로도 동일하게 server 계약만 신뢰하도록 정합한다.
 */
export const canRenderRegistrationSession = (session: CardRegistrationSession) =>
  session.gateway === 'bloomin' && session.requiresCardInput !== false;

/**
 * Bloomin 카드 등록 payload 검증 + 정규화 (#430 — 필드 단위 에러 수집).
 *
 * 모든 필드를 한 번에 검사해 에러 맵을 반환한다. fail-open 금지: 에러가 하나라도
 * 있으면 payload 미생성 → 제출 차단. CVV 는 PAN prefix 로 검출한 brand 별 정확
 * 길이를 강제한다 (visa/mc 3 / amex 4).
 */
export const buildBloominCardRegistrationPayload = (
  card: BloominCardFormState,
  messages: BloominValidationMessages,
  now: Date = new Date()
): BloominPayloadBuildResult => {
  const tarjeta = digitsOnly(card.cardNumber);
  const rawMonth = digitsOnly(card.expiryMonth);
  const monthNumber = Number(rawMonth);
  const month = String(monthNumber).padStart(2, '0');
  const year = normalizeExpiryYear(card.expiryYear);
  const digitos = digitsOnly(card.cvv);
  const titular = card.holder.trim().toUpperCase();

  const errors: BloominCardFieldErrors = {};

  if (!/^\d{13,19}$/.test(tarjeta) || !isValidLuhn(tarjeta)) {
    errors.cardNumber = messages.invalidCard;
  }

  if (
    !/^\d{1,2}$/.test(rawMonth) ||
    !isExpiryInFuture(monthNumber, year, now) ||
    year > now.getFullYear() + 20
  ) {
    errors.expiry = messages.invalidExpiry;
  }

  if (!isValidCvv(digitos, detectCardBrand(tarjeta))) {
    errors.cvv = messages.invalidCvv;
  }

  if (titular.length === 0) {
    errors.holder = messages.invalidHolder;
  }

  if (Object.keys(errors).length > 0) {
    return { payload: null, errors };
  }

  return {
    payload: {
      tarjeta,
      mes: month,
      anio: year,
      digitos,
      titular,
    },
    errors: null,
  };
};
